import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { vehicleApi, featuredInventoryApi } from '../services/api';
import VehicleCard from '../components/VehicleCard';
import LoadingSpinner from '../components/LoadingSpinner';
import CurrencyRates from '../components/CurrencyRates';
import { convertToBgn, convertToEur } from '../utils/currency';
import { 
  ClockIcon, 
  TruckIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';
import ViberPopup from '../components/ViberPopup';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch data for homepage
  const { data: statistics, isLoading: statsLoading } = useQuery(
    'statistics',
    vehicleApi.getStatistics
  );

  // Featured inventory vehicles (clean cars for sale)
  const { data: featuredInventory, isLoading: inventoryLoading } = useQuery(
    'featured-inventory-carousel',
    featuredInventoryApi.getHomepageCarousel
  );

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/vehicles?keyword=${encodeURIComponent(searchQuery)}`);
    }
  };

  // Supercars - luxury/high-end vehicles from both Canada and USA (show only 4 for 1 row)
  const { data: supercars, isLoading: supercarsLoading } = useQuery(
    'supercars',
    async () => {
      // Use backend is_luxury filter - fetches ALL luxury vehicles from DB
      // Backend filters by brands: FERRARI, LAMBORGHINI, PORSCHE, BENTLEY, etc.
      // And models: 911, AMG, CORVETTE, HELLCAT, VIPER, etc.
      const response = await vehicleApi.searchVehicles({ 
        page: 1,
        page_size: 50,
        is_luxury: true
      });
      
      const luxuryFiltered = response.items;
      
      // Group by make to ensure variety
      const byMake: { [key: string]: any[] } = {};
      luxuryFiltered.forEach(v => {
        const make = v.make?.toUpperCase() || 'OTHER';
        if (!byMake[make]) byMake[make] = [];
        byMake[make].push(v);
      });
      
      // Pick one random vehicle from each make first
      const makes = Object.keys(byMake).sort(() => Math.random() - 0.5);
      const selected: any[] = [];
      const usedIds = new Set<number>();
      
      // First pass: one from each make
      for (const make of makes) {
        if (selected.length >= 4) break;
        const vehicles = byMake[make];
        const randomVehicle = vehicles[Math.floor(Math.random() * vehicles.length)];
        selected.push(randomVehicle);
        usedIds.add(randomVehicle.id);
      }
      
      // Second pass: fill remaining slots with more vehicles from any make
      if (selected.length < 4) {
        const remaining = luxuryFiltered
          .filter(v => !usedIds.has(v.id))
          .sort(() => Math.random() - 0.5);
        for (const vehicle of remaining) {
          if (selected.length >= 4) break;
          selected.push(vehicle);
        }
      }
      
      return {
        items: selected,
        total: luxuryFiltered.length,
        page: 1,
        page_size: 4,
        total_pages: 1
      };
    }
  );

  // Buy Now vehicles - use API filter to get only buy now vehicles
  const { data: buyNowVehicles, isLoading: buyNowLoading } = useQuery(
    'buy-now-vehicles',
    async () => {
      // Use the is_buy_now filter - fetch multiple pages to get all
      const allBuyNow: any[] = [];
      for (let page = 1; page <= 4; page++) {
        const response = await vehicleApi.searchVehicles({ 
          page,
          page_size: 50,
          is_buy_now: true
        });
        allBuyNow.push(...response.items);
        if (response.items.length < 50) break;
      }
      
      // Fisher-Yates shuffle for true randomization
      const shuffled = [...allBuyNow];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      
      // Now pick ensuring variety of makes
      const selected: any[] = [];
      const usedMakes = new Set<string>();
      
      // First pass: one from each unique make
      for (const vehicle of shuffled) {
        if (selected.length >= 4) break;
        const make = vehicle.make?.toUpperCase() || 'OTHER';
        if (!usedMakes.has(make)) {
          selected.push(vehicle);
          usedMakes.add(make);
        }
      }
      
      // Second pass: fill remaining slots if less than 4 unique makes
      if (selected.length < 4) {
        const usedIds = new Set(selected.map(v => v.id));
        for (const vehicle of shuffled) {
          if (selected.length >= 4) break;
          if (!usedIds.has(vehicle.id)) {
            selected.push(vehicle);
          }
        }
      }
      
      return {
        items: selected,
        total: allBuyNow.length
      };
    }
  );

  // Ending Soon - auctions ending in the next 48+ hours (show only 4 for 1 row)
  const { data: endingSoonVehicles, isLoading: endingSoonLoading } = useQuery(
    'ending-soon',
    async () => {
      // Fetch multiple pages to find upcoming auctions
      const allVehicles: any[] = [];
      for (let page = 1; page <= 5; page++) {
        const response = await vehicleApi.searchVehicles({ 
          page,
          page_size: 100
        });
        allVehicles.push(...response.items);
        if (response.items.length < 100) break;
      }
      
      // Filter for vehicles with upcoming auction dates
      const now = new Date();
      
      const upcomingAuctions = allVehicles.filter(v => {
        if (!v.auction_date) return false;
        const auctionDate = new Date(v.auction_date);
        // Show auctions that are in the future
        return auctionDate > now;
      });
      
      // Sort by auction date (soonest first)
      upcomingAuctions.sort((a, b) => {
        const dateA = new Date(a.auction_date!).getTime();
        const dateB = new Date(b.auction_date!).getTime();
        return dateA - dateB;
      });
      
      return upcomingAuctions.slice(0, 4);
    }
  );

  const statsCards = [
    {
      name: 'Автомобили',
      value: statistics?.total_vehicles || 0,
      icon: TruckIcon,
      color: 'bg-blue-500',
    },
    {
      name: 'Активни Търгове',
      value: statistics?.active_auctions || 0,
      icon: ClockIcon,
      color: 'bg-green-500',
    },
  ];

  // Premium Brands with logos
  const premiumBrands = [
    { name: 'Mercedes-Benz', make: 'MERCEDES', logo: 'https://www.carlogos.org/car-logos/mercedes-benz-logo.png', bg: 'bg-gradient-to-br from-gray-800 to-black', invert: true },
    { name: 'BMW', make: 'BMW', logo: 'https://www.carlogos.org/car-logos/bmw-logo.png', bg: 'bg-gradient-to-br from-slate-100 to-slate-200', invert: false },
    { name: 'Audi', make: 'AUDI', logo: 'https://www.carlogos.org/car-logos/audi-logo.png', bg: 'bg-gradient-to-br from-gray-700 to-gray-900', invert: true },
    { name: 'Porsche', make: 'PORSCHE', logo: 'https://www.carlogos.org/car-logos/porsche-logo.png', bg: 'bg-gradient-to-br from-amber-100 to-amber-200', invert: false },
    { name: 'Ford', make: 'FORD', logo: 'https://www.carlogos.org/car-logos/ford-logo.png', bg: 'bg-gradient-to-br from-slate-100 to-slate-200', invert: false },
    { name: 'Dodge', make: 'DODGE', logo: 'https://www.carlogos.org/car-logos/dodge-logo.png', bg: 'bg-gradient-to-br from-red-700 to-red-900', invert: true },
    { name: 'Chevrolet', make: 'CHEVROLET', logo: 'https://www.carlogos.org/car-logos/chevrolet-logo.png', bg: 'bg-gradient-to-br from-slate-100 to-slate-200', invert: false },
    { name: 'Toyota', make: 'TOYOTA', logo: 'https://www.carlogos.org/car-logos/toyota-logo.png', bg: 'bg-gradient-to-br from-gray-100 to-gray-200', invert: false },
    { name: 'Honda', make: 'HONDA', logo: 'https://www.carlogos.org/car-logos/honda-logo.png', bg: 'bg-gradient-to-br from-red-600 to-red-800', invert: true },
    { name: 'Lexus', make: 'LEXUS', logo: 'https://www.carlogos.org/car-logos/lexus-logo.png', bg: 'bg-gradient-to-br from-slate-700 to-slate-900', invert: true },
    { name: 'Tesla', make: 'TESLA', logo: 'https://www.carlogos.org/car-logos/tesla-logo.png', bg: 'bg-gradient-to-br from-red-700 to-red-950', invert: true },
    { name: 'Jeep', make: 'JEEP', logo: 'https://www.carlogos.org/car-logos/jeep-logo.png', bg: 'bg-gradient-to-br from-green-700 to-green-900', invert: true },
    { name: 'Cadillac', make: 'CADILLAC', logo: 'https://www.carlogos.org/car-logos/cadillac-logo.png', bg: 'bg-gradient-to-br from-slate-100 to-slate-200', invert: false },
    { name: 'Volkswagen', make: 'VOLKSWAGEN', logo: 'https://www.carlogos.org/car-logos/volkswagen-logo.png', bg: 'bg-gradient-to-br from-slate-100 to-slate-200', invert: false },
    { name: 'Hyundai', make: 'HYUNDAI', logo: 'https://www.carlogos.org/car-logos/hyundai-logo.png', bg: 'bg-gradient-to-br from-gray-100 to-gray-200', invert: false },
    { name: 'GMC', make: 'GMC', logo: 'https://www.carlogos.org/car-logos/gmc-logo.png', bg: 'bg-gradient-to-br from-red-700 to-red-900', invert: true },
  ];

  // Budget filters (in BGN with EUR equivalent)
  const budgetRanges = [
    { label: 'Под 20,000 лв', sublabel: '(~10,000 €)', min: 0, max: 10000, icon: '💵' },
    { label: '20,000 - 40,000 лв', sublabel: '(~10,000-20,000 €)', min: 10000, max: 20000, icon: '💰' },
    { label: '40,000 - 80,000 лв', sublabel: '(~20,000-40,000 €)', min: 20000, max: 40000, icon: '💎' },
    { label: 'Над 80,000 лв', sublabel: '(~40,000+ €)', min: 40000, max: 999999, icon: '👑' },
  ];

  return (
    <div className="min-h-screen">
      <ViberPopup />
      {/* Enhanced Hero Section with Background Image */}
      <div className="relative text-white overflow-hidden min-h-[500px] md:min-h-[650px] lg:min-h-[750px]">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('/images/hero-bg.png')" }}
        ></div>
        {/* Dark Overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/80"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            {/* Centered Logo */}
            <div className="flex justify-center mb-2">
              <img 
                src="/transparentlogo.png" 
                alt="CarCube" 
                className="h-40 sm:h-56 md:h-72 lg:h-96 w-auto max-w-[85vw] drop-shadow-2xl"
              />
            </div>
            <h1 className="text-3xl md:text-5xl font-extrabold mb-2 text-white">
              Автомобили от САЩ и Канада
            </h1>
            <p className="text-lg md:text-xl text-gray-200 mb-6 max-w-3xl mx-auto">
              Намерете мечтаната си кола на топ цена
            </p>
            
            {/* Search Bar */}
            <form onSubmit={handleSearch} className="max-w-2xl mx-auto mb-6">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Търсене по марка, модел или ключова дума..."
                  className="w-full px-5 py-4 pr-12 rounded-xl text-gray-900 text-base focus:outline-none focus:ring-4 focus:ring-primary-400 shadow-2xl"
                />
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-primary-600 hover:bg-primary-500 text-white p-2.5 rounded-lg transition-colors duration-200"
                >
                  <MagnifyingGlassIcon className="w-5 h-5" />
                </button>
              </div>
            </form>

            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                to="/vehicles"
                className="bg-white text-primary-700 px-8 py-4 rounded-xl font-bold hover:bg-gray-100 hover:scale-105 transition-all duration-200 shadow-lg"
              >
                Всички автомобили
              </Link>
              <Link
                to="/available"
                className="bg-primary-600/80 backdrop-blur-sm text-white px-8 py-4 rounded-xl font-bold hover:bg-primary-500 border-2 border-white/30 hover:scale-105 transition-all duration-200 shadow-lg"
              >
                Налични автомобили
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats Bar */}
      <div className="bg-white border-b border-gray-200 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-8">
            {statsCards.map((stat) => (
              <div key={stat.name} className="flex items-center">
                <div className={`${stat.color} rounded-lg p-2`}>
                  <stat.icon className="h-5 w-5 text-white" />
                  </div>
                <div className="ml-3">
                  <p className="text-xs text-gray-500">{stat.name}</p>
                  <p className="text-lg font-bold text-gray-900">
                    {statsLoading ? '...' : stat.value}
                  </p>
                </div>
              </div>
            ))}
            <div className="h-8 w-px bg-gray-300 hidden sm:block"></div>
            <CurrencyRates compact />
          </div>
        </div>
      </div>

      {/* Buy Now Vehicles */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">💰 Купи Сега</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Без търг! Купете тези автомобили веднага на фиксирана цена.
            </p>
          </div>

          {buyNowLoading ? (
            <div className="flex justify-center">
              <LoadingSpinner />
            </div>
          ) : buyNowVehicles?.items && buyNowVehicles.items.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {buyNowVehicles.items.slice(0, 4).map((vehicle) => (
                  <VehicleCard key={vehicle.id} vehicle={vehicle} />
                ))}
              </div>
              <div className="text-center">
                <Link
                  to="/vehicles?is_buy_now=true"
                  className="btn-primary"
                >
                  Виж всички "Купи Сега"
                </Link>
              </div>
            </>
          ) : (
            <div className="text-center text-gray-500">
              <p>Няма налични автомобили за директна покупка в момента</p>
            </div>
          )}
        </div>
      </div>

      {/* Supercars */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">🏎️ Суперколи и Луксозни Автомобили</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Ексклузивна колекция от високопроизводителни и луксозни автомобили от премиум марки.
            </p>
          </div>

          {supercarsLoading ? (
            <div className="flex justify-center">
              <LoadingSpinner />
            </div>
          ) : supercars?.items && supercars.items.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {supercars.items.slice(0, 4).map((vehicle) => (
                  <VehicleCard key={vehicle.id} vehicle={vehicle} />
                ))}
              </div>
              <div className="text-center">
                <Link
                  to="/vehicles?is_luxury=true"
                  className="btn-primary"
                >
                  Виж всички луксозни автомобили
                </Link>
              </div>
            </>
          ) : (
            <div className="text-center text-gray-500">
              <p>Няма налични суперколи в момента</p>
            </div>
          )}
        </div>
      </div>

      {/* Ending Soon */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">⏰ Приключват Скоро</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Активни търгове, които приключват скоро. Направете офертата си преди да е късно!
            </p>
          </div>

          {endingSoonLoading ? (
            <div className="flex justify-center">
              <LoadingSpinner />
            </div>
          ) : endingSoonVehicles && endingSoonVehicles.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {endingSoonVehicles.slice(0, 4).map((vehicle) => (
                  <VehicleCard key={vehicle.id} vehicle={vehicle} showCountdown />
                ))}
              </div>
              <div className="text-center">
                <Link
                  to="/vehicles?ending_soon=true"
                  className="btn-primary"
                >
                  Виж всички приключващи скоро
                </Link>
              </div>
            </>
          ) : (
            <div className="text-center text-gray-500">
              <p>Няма активни търгове в момента</p>
            </div>
          )}
        </div>
      </div>

      {/* Featured Vehicle Carousel - Hidden until we have featured vehicles */}
      {/* Section temporarily hidden */}

      {/* Browse by Premium Brands */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Премиум Марки</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Разгледайте автомобили от най-престижните марки в света
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
            {premiumBrands.map((brand) => (
              <Link
                key={brand.make}
                to={`/vehicles?make=${brand.make}`}
                className="group relative overflow-hidden rounded-xl shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className={`h-28 ${brand.bg} p-4 flex flex-col items-center justify-center`}>
                  {/* Brand logo */}
                  <img 
                    src={brand.logo} 
                    alt={brand.name}
                    className={`w-12 h-12 object-contain transition-transform duration-300 group-hover:scale-110 ${brand.invert ? 'filter brightness-0 invert' : ''}`}
                  />
                  {/* Brand name */}
                  <h3 className={`text-xs font-semibold mt-2 text-center truncate w-full ${brand.invert ? 'text-white' : 'text-gray-800'}`}>{brand.name}</h3>
                </div>
              </Link>
                ))}
              </div>
        </div>
      </div>

      {/* Search by Budget - Buy Now Vehicles Only */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Търсене по Бюджет</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {budgetRanges.map((range, idx) => (
                <Link
                key={idx}
                to={`/vehicles?is_buy_now=true&min_buy_now=${range.min}&max_buy_now=${range.max}`}
                className="bg-white rounded-xl shadow-md hover:shadow-xl p-8 text-center transition-all duration-300 hover:-translate-y-1 border-2 border-transparent hover:border-primary-500"
                >
                <div className="text-5xl mb-4">{range.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">{range.label}</h3>
                <p className="text-xs text-gray-500 mb-2">{range.sublabel}</p>
                <p className="text-sm text-gray-600">Виж "Купи сега" автомобили</p>
                </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Featured Inventory - Clean Cars for Sale (at the end) */}
      {featuredInventory && featuredInventory.length > 0 && (
        <div className="bg-gradient-to-b from-gray-50 to-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center px-4 py-2 bg-green-100 text-green-700 rounded-full mb-4">
                <SparklesIcon className="w-5 h-5 mr-2" />
                <span className="font-semibold">Премиум Селекция</span>
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Налични Автомобили
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Автомобили в отлично състояние, готови за покупка в България
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredInventory.slice(0, 4).map((vehicle: any) => (
                <Link 
                  key={vehicle.id}
                  to={`/featured-inventory/${vehicle.id}`}
                  className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden border border-gray-200"
                >
                  {/* Image */}
                  <div className="relative h-48 bg-gray-200">
                    {vehicle.images && vehicle.images.length > 0 ? (
                      <img
                        src={vehicle.images[0].image_url}
                        alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x300?text=No+Image';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <TruckIcon className="w-16 h-16 text-gray-400" />
                      </div>
                    )}
                    <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded text-xs font-semibold">
                      Наличен
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    {/* Title */}
                    <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">
                      {vehicle.year} {vehicle.make} {vehicle.model}
                    </h3>

                    {/* Info Grid */}
                    <div className="space-y-2 mb-3">
                      {vehicle.odometer && (
                        <div className="flex items-center text-sm text-gray-600">
                          <ClockIcon className="w-4 h-4 mr-1" />
                          {vehicle.odometer.toLocaleString()} {vehicle.odometer_unit}
                        </div>
                      )}
                      {vehicle.location && (
                        <div className="flex items-center text-sm text-gray-600">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          {vehicle.location}
              </div>
                      )}
                    </div>

                    {/* Condition Badge */}
                    <div className="flex items-center mb-3">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Налични автомобил
                      </span>
                    </div>

                    {/* Price */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                      <div className="flex items-center">
                        <CurrencyDollarIcon className="w-5 h-5 mr-1 text-green-600" />
                        <span className="text-xl font-bold text-green-600">
                          {Math.round(vehicle.price).toLocaleString()} €
                        </span>
                        <span className="text-xs text-gray-500 ml-2">
                          ({Math.round(vehicle.price * 1.96).toLocaleString()} лв)
                        </span>
                      </div>
                      <span className="text-sm font-medium text-green-600">Купи сега</span>
                    </div>
                  </div>
                </Link>
              ))}
        </div>
      </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
